export type LetterStarMotion = 'quiet' | 'standard' | 'rich';

export interface LetterAnimationSettings {
  starCount: number;
  starMotion: LetterStarMotion;
  starlightStarTwinkle: boolean;
  /** 星空主题背景星点密度，所有可调动效设置统一由动效管理持有。 */
  starlightDensity: number;
  /** 星空星点呼吸频率；与密度独立。 */
  starlightTwinkleSpeed: number;
}

export const DEFAULT_LETTER_ANIMATION_SETTINGS: LetterAnimationSettings = {
  starCount: 24,
  starMotion: 'rich',
  starlightStarTwinkle: true,
  starlightDensity: 55,
  starlightTwinkleSpeed: 50,
};

const STORAGE_KEY = 'prologue.animation-settings.letter';
export const ANIMATION_SETTINGS_CHANGE_EVENT = 'prologue:animation-settings-change';
const MIN_STAR_COUNT = 8;
const MAX_STAR_COUNT = 60;

export const STAR_DENSITY_STORAGE_KEY = 'prologue.animation-settings.starlight-density';
export const DEFAULT_STAR_DENSITY = DEFAULT_LETTER_ANIMATION_SETTINGS.starlightDensity;
export const DEFAULT_STAR_TWINKLE_SPEED = DEFAULT_LETTER_ANIMATION_SETTINGS.starlightTwinkleSpeed;

function clampStarCount(value: unknown): number {
  const count = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_LETTER_ANIMATION_SETTINGS.starCount;
  return Math.min(MAX_STAR_COUNT, Math.max(MIN_STAR_COUNT, count));
}

function isStarMotion(value: unknown): value is LetterStarMotion {
  return value === 'quiet' || value === 'standard' || value === 'rich';
}

function clampStarlightDensity(value: unknown): number {
  const density = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_STAR_DENSITY;
  return Math.min(100, Math.max(0, density));
}

function clampTwinkleSpeed(value: unknown): number {
  const speed = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : DEFAULT_STAR_TWINKLE_SPEED;
  return Math.min(100, Math.max(0, speed));
}

export function loadLetterAnimationSettings(): LetterAnimationSettings {
  if (typeof window === 'undefined') return DEFAULT_LETTER_ANIMATION_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_LETTER_ANIMATION_SETTINGS;
    const parsed = JSON.parse(raw) as {
      starCount?: unknown;
      starMotion?: unknown;
      starlightStarTwinkle?: unknown;
      starlightDensity?: unknown;
      starlightTwinkleSpeed?: unknown;
    };
    const legacyDensity = window.localStorage.getItem('prologue.theme.starlight-density');
    return {
      starCount: clampStarCount(parsed.starCount),
      starMotion: isStarMotion(parsed.starMotion)
        ? parsed.starMotion
        : DEFAULT_LETTER_ANIMATION_SETTINGS.starMotion,
      starlightStarTwinkle: typeof parsed.starlightStarTwinkle === 'boolean'
        ? parsed.starlightStarTwinkle
        : DEFAULT_LETTER_ANIMATION_SETTINGS.starlightStarTwinkle,
      starlightDensity: clampStarlightDensity(parsed.starlightDensity ?? (legacyDensity === null ? undefined : Number(legacyDensity))),
      starlightTwinkleSpeed: clampTwinkleSpeed(parsed.starlightTwinkleSpeed),
    };
  } catch {
    return DEFAULT_LETTER_ANIMATION_SETTINGS;
  }
}

export function saveLetterAnimationSettings(settings: LetterAnimationSettings): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      starCount: clampStarCount(settings.starCount),
      starMotion: isStarMotion(settings.starMotion)
        ? settings.starMotion
        : DEFAULT_LETTER_ANIMATION_SETTINGS.starMotion,
      starlightStarTwinkle: settings.starlightStarTwinkle
        ?? DEFAULT_LETTER_ANIMATION_SETTINGS.starlightStarTwinkle,
      starlightDensity: clampStarlightDensity(settings.starlightDensity),
      starlightTwinkleSpeed: clampTwinkleSpeed(settings.starlightTwinkleSpeed),
    }));
    window.dispatchEvent(new Event(ANIMATION_SETTINGS_CHANGE_EVENT));
  } catch {
    // 无法使用本地存储时保留当前页内状态，不影响动效使用。
  }
}
